import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Chip,
  Box,
  makeStyles,
  Grid,
} from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { useAnnouncements } from '@procore-oss/backstage-plugin-announcements-react';
import { DateTime } from 'luxon';
import type { Announcement } from '@procore-oss/backstage-plugin-announcements-common';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.background.default,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.divider,
      borderRadius: '4px',
    },
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  },
  cardAction: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  dateAndAuthor: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.4,
    fontSize: '0.95rem',
    color: theme.palette.primary.main,
  },
  excerpt: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    flexGrow: 1,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0.25),
    lineHeight: 1.3,
  },
  tags: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.5),
  },
  tag: {
    height: 20,
    fontSize: '0.7rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

const categoryColors: Record<string, 'primary' | 'secondary' | 'default'> = {
  announcement: 'primary',
  update: 'secondary',
  maintenance: 'default',
  news: 'primary',
  alert: 'secondary',
};

/**
 * Extracts username from publisher string, removing @avidxchange.com and "Avidxchange.Com"
 * Handles various formats and cleans up the display name
 */
const extractUsername = (publisher: string): string => {
  if (!publisher) return 'Unknown';
  
  // Extract everything after the last '/' if present
  const parts = publisher.split('/');
  let username = parts[parts.length - 1];
  
  // Remove @avidxchange.com or any other email domain
  username = username.split('@')[0];
  
  // Remove "Avidxchange.Com" or "Avidxchange" if it appears
  username = username.replace(/avidxchange\.com/gi, '').replace(/avidxchange/gi, '');
  
  // Clean up any remaining dots, underscores, or extra spaces
  username = username.replace(/[._]/g, ' ').trim();
  
  // If empty after cleanup, return Unknown
  if (!username) return 'Unknown';
  
  // Return lowercase username
  return username.toLowerCase();
};

interface AnnouncementsCardGridProps {
  max?: number;
  category?: string;
  active?: boolean;
}

/**
 * A modern card-based layout for displaying announcements.
 * Styled to match the AI Central blog post cards with:
 * - Small date/author at top
 * - Bold blue title
 * - Gray excerpt text
 * - Category badges/pills at bottom
 * 
 * Uses the stable useAnnouncements hook from @procore-oss/backstage-plugin-announcements-react
 * and Announcement types from @procore-oss/backstage-plugin-announcements-common.
 * 
 * This component is designed to be resilient to plugin upgrades by:
 * - Using the public, stable useAnnouncements hook API
 * - Importing types from the -common package
 * - Gracefully handling missing or optional fields
 * - Using safe property access patterns
 */
export const AnnouncementsCardGrid = ({ 
  max = 6, 
  category,
  active
}: AnnouncementsCardGridProps) => {
  const classes = useStyles();
  const { announcements, loading, error } = useAnnouncements({ 
    max, 
    category, 
    ...(active !== undefined && { active }),
    orderBy: 'created_at',
    orderByDirection: 'desc'
  });

  if (loading) {
    return (
      <Box className={classes.loadingState}>
        <Typography>Loading announcements...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.emptyState}>
        <Typography color="error">Failed to load announcements</Typography>
      </Box>
    );
  }

  // Handle both array and results object format for future-proofing
  const announcementsList = Array.isArray(announcements) 
    ? announcements 
    : (announcements as any)?.results || [];

  // Sort by created_at descending (most recent first)
  const sortedAnnouncements = [...announcementsList].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });

  if (!sortedAnnouncements || sortedAnnouncements.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography>No announcements at this time</Typography>
      </Box>
    );
  }

  const formatRelativeDate = (dateString: string) => {
    try {
      return DateTime.fromISO(dateString).toRelative() || dateString;
    } catch {
      return dateString;
    }
  };

  return (
    <Box className={classes.container}>
      <Grid container spacing={2}>
        {sortedAnnouncements.map((announcement: Announcement) => {
          const categoryTitle = announcement.category?.title || 'Announcement';
          const categoryColor = categoryColors[categoryTitle.toLowerCase()] || 'default';
          const username = extractUsername(announcement.publisher);

          return (
            <Grid item xs={12} sm={6} md={4} key={announcement.id}>
              <Card className={classes.card}>
                <CardActionArea
                  className={classes.cardAction}
                  component={Link}
                  to={`/announcements/view/${announcement.id}`}
                >
                  <CardContent className={classes.content}>
                    <Typography className={classes.dateAndAuthor}>
                      {formatRelativeDate(announcement.created_at)} • {username}
                    </Typography>
                    <Typography variant="h6" className={classes.title}>
                      {announcement.title}
                    </Typography>
                    {announcement.excerpt && (
                      <Typography className={classes.excerpt}>
                        {announcement.excerpt}
                      </Typography>
                    )}
                    <Box className={classes.tags}>
                      <Chip
                        label={categoryTitle}
                        color={categoryColor}
                        size="small"
                        className={classes.tag}
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
